import type NativeArray from '@ember/array/-private/native-array';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { Task } from 'ember-concurrency';
import type AgreementModel from 'garaje/models/agreement';
import type FlowModel from 'garaje/models/flow';
import type GlobalAgreementModel from 'garaje/models/global-agreement';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SubscriptionModel from 'garaje/models/subscription';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import fixVideoUrl from 'garaje/utils/fix-video-url';
import LOCALE_OPTIONS, { isRTLLocale } from 'garaje/utils/locale-options';
import { sanitizeMarkdown } from 'garaje/utils/sanitize-markdown';
import uniq from 'lodash/uniq';
import { not } from 'macro-decorators';
const { escapeExpression } = (<typeof Handlebars>(<unknown>Ember.Handlebars)).Utils;

const MINIMUM_IPAD_VERSION_NEEDED = '3.3.6';

type SupportedAgreements = AgreementModel | GlobalAgreementModel;

type EditableTextFields = 'body' | 'footer';

interface EditTextComponentSignature {
  Args: {
    translationLanguagesAvailable: boolean;
    translationEnabledLocales: string[];
    defaultLocale: string;
    changeset: DetailedChangeset<SupportedAgreements>;
    flowName: string;
    flow: FlowModel;
    isAppUpdateRequired: boolean;
    model: SupportedAgreements;
    signInFields: NativeArray<SignInFieldModel>;
    propagable: boolean;
    tracking: Record<string, unknown>;
    trackLegalDocument: (model: SupportedAgreements) => void;
    vrSubscription: SubscriptionModel;
    isDisabled: boolean;
    updateAndSaveTask: Task<
      void,
      [model: SupportedAgreements, field: keyof SupportedAgreements | null, value: unknown, propagable: boolean]
    >;
  };
}

export default class EditTextComponent extends Component<EditTextComponentSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked selectedLocale;
  @tracked textWasEdited = false;
  @tracked isEditing = true;
  @tracked localeOptions = LOCALE_OPTIONS;
  @tracked ndaTextFormatted?: string;

  iPadVersionRequired = MINIMUM_IPAD_VERSION_NEEDED;

  @not('hasInvalidVideoUrl') hasValidVideoUrl!: boolean;

  constructor(owner: unknown, args: EditTextComponentSignature['Args']) {
    super(owner, args);
    this.selectedLocale = this.args.defaultLocale;
  }

  get isRTLLocale(): boolean {
    return isRTLLocale(this.selectedLocale);
  }

  get today(): string {
    const date = new Date();
    return date.toLocaleDateString(this.selectedLocale, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  get isSaveDisabled(): boolean {
    const isRunning = this.args.updateAndSaveTask.isRunning;
    if (this.textWasEdited && !isRunning) {
      return false;
    } else {
      const isPristine = this.args.changeset.isPristine;
      const isInvalid = this.args.changeset.isInvalid;
      return isPristine || isInvalid || isRunning;
    }
  }

  get availableLocales(): Array<{ label: string; value: string }> {
    const { defaultLocale, translationEnabledLocales, flow } = this.args;
    let locales = translationEnabledLocales;

    if (flow.isGlobal) {
      locales = uniq([translationEnabledLocales, defaultLocale].flat());
    }

    return this.localeOptions.filter((option) => locales.includes(option.value));
  }

  get messageValue(): string | undefined {
    if (this.args.defaultLocale === this.selectedLocale) {
      return this.args.changeset.body;
    }
    const customTranslations = this.args.changeset.customTranslations;
    if (isBlank(customTranslations['body']) || typeof customTranslations['body'][this.selectedLocale] !== 'string') {
      return '';
    }
    return this.args.changeset.customTranslations['body'][this.selectedLocale];
  }

  get footerMessageValue(): string | undefined {
    if (this.args.defaultLocale === this.selectedLocale) {
      return this.args.changeset.footer;
    }
    const customTranslations = this.args.changeset.customTranslations;
    if (
      isBlank(customTranslations['footer']) ||
      typeof customTranslations['footer']?.[this.selectedLocale] !== 'string'
    ) {
      return '';
    }
    return this.args.changeset.customTranslations['footer']?.[this.selectedLocale];
  }

  get variables(): string[] {
    const variables = ['date', 'company', 'name', 'field purpose of visit'];

    this.args.signInFields?.sortBy('position').forEach((field) => {
      if (field.isCustom) {
        variables.push(`field ${field.name}`);
      } else if (field.kind === 'email') {
        variables.push('email');
      } else if (field.kind === 'phone') {
        variables.push('phone');
      }
    });

    return variables.map((v) => `::${v.toUpperCase()}::`);
  }

  get hasInvalidVideoUrl(): boolean {
    const errors = this.args.changeset.errors;
    return errors.some(({ key }) => key === 'videoUrl');
  }

  get canRequireResign(): boolean {
    const changes = Object.keys(this.args.changeset.change || {});
    return changes.includes('body') || changes.includes('videoUrl');
  }

  #changeLocale(value: string, key: EditableTextFields): void {
    if (this.args.defaultLocale === this.selectedLocale) {
      this.args.changeset[key] = value;
    } else {
      const customTranslations = Object.assign({}, this.args.changeset.customTranslations);
      if (isBlank(customTranslations[key])) {
        customTranslations[key] = {};
      }
      customTranslations[key]![this.selectedLocale] = value;
      this.args.changeset.customTranslations = customTranslations;
    }
  }

  useEmbeddedVideoUrl(): void {
    const url = this.args.changeset.videoUrl;
    if (url && this.hasValidVideoUrl) {
      const fixed = fixVideoUrl(url);
      if (url !== fixed) {
        this.args.changeset.videoUrl = fixed;
      }
    }
  }

  @action
  textChanged(key: EditableTextFields, value: string): void {
    this.textWasEdited = true;
    this.args.changeset.requireResign = true;
    if (this.args.translationLanguagesAvailable) {
      this.#changeLocale(value, key);
    } else {
      this.args.changeset[key] = value;
    }
  }

  @action
  applyChangeset(changeset: DetailedChangeset<SupportedAgreements>): void {
    changeset.execute();
    changeset.rollback();
  }

  @action
  formatNDAText(model: SupportedAgreements): void {
    const companyName = this.currentLocation.location?.companyName;
    const date = this.today;
    const defaultFields = ['DATE', 'COMPANY', 'NAME', 'EMAIL', 'PHONE'];

    const escapedCustomFields = this.args.signInFields.filterBy('isCustom').map((field) => {
      // eslint-disable-next-line no-useless-escape
      return field.name.toUpperCase().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    });

    const fieldsToMatch = defaultFields
      .concat(escapedCustomFields.map((field) => `FIELD ${field}`))
      .concat(['FIELD PURPOSE OF VISIT'])
      .join('|');

    let text: string | undefined = model.body;
    if (this.currentLocation.location?.locale !== this.selectedLocale) {
      const customTranslations = model.customTranslations;
      text = customTranslations.body[this.selectedLocale];
    }

    const formattedText = sanitizeMarkdown(text!)
      .replace(new RegExp(`__(${fieldsToMatch})__`, 'g'), '**::$1::**')
      .replace(/::DATE::/g, date)
      .replace(/::COMPANY::/g, escapeExpression(companyName!))
      .replace(/::FIELD PURPOSE OF VISIT::/g, escapeExpression(this.args.flowName))
      .replace(/::(NAME|EMAIL|PHONE)::/g, 'Visitor:$1')
      .replace(new RegExp(`::FIELD (${escapedCustomFields.join('|')})::`, 'g'), 'Visitor:$1');

    // Passed to <MarkdownToHtml>
    this.ndaTextFormatted = formattedText;
  }

  @action
  onToPreviewing(): void {
    const { eventName } = this.args.tracking;
    this.args.tracking['eventName'] = 'Legal Docs - Legal Document Previewed';
    this.args.trackLegalDocument(this.args.changeset);
    this.args.tracking['eventName'] = eventName;
  }

  @action
  onToEditing(): void {
    this.args.tracking['eventName'] = 'Legal Docs - Legal Document Edited';
  }

  @action
  performSave(): void {
    this.useEmbeddedVideoUrl();
    this.applyChangeset(this.args.changeset);
    void this.args.updateAndSaveTask.perform(this.args.model, null, null, this.args.propagable).then(() => {
      this.textWasEdited = false;
      this.args.trackLegalDocument(this.args.model);
    });
  }

  @action
  updateName(name: string): void {
    this.args.changeset.name = name;
  }

  @action
  updateVideoUrl(url: string): void {
    this.args.changeset.videoUrl = url;
  }

  @action
  updateRequireResign(checked: boolean): void {
    this.args.changeset.requireResign = checked;
  }
}
